import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useForm } from "react-hook-form";
import { SubmitHandler } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { optionsStatus } from "~/pages/Editor/EditorCatalogEducationalPrograms/options";
import {
  CurriculumManagementProps,
  IGroup,
  SelectOptions,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/types";
import {
  transformStatus,
  useGetGroup,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/utils";
import { OpenModal } from "~/pages/Editor/type";
import {
  useCreateCurriculumGroupMutation,
  useEditCurriculumGroupMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { addGMTOffsetToDate } from "~/shared/lib/addGMTOffsetToDate";
import { FormStatus } from "~/shared/lib/statuses";
import { Field } from "~/shared/ui/Field";

import { schema } from "./options";

import s from "~/pages/Editor/EditorCatalogEducationalPrograms/ManagementForm.less";

export type FormSubmit = {
  name: string;
  status: SelectOptions;
  sequenceNumber: number;
};

const defaultValues = {
  name: "",
  status: { label: "", value: "" },
  sequenceNumber: null,
};

export const GroupsManagement = ({
  formId,
  refetch,
  closeModal,
}: CurriculumManagementProps) => {
  const [currentGroup, setCurrentGroup] = useState<IGroup>(null);

  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotify();
  const { openModal, currentId } = useGetLocationData();

  const currentDate = `${new Date()}`;
  const { group, refetch: refetchGroup } = useGetGroup(currentId);

  const { handleSubmit, control, setValue, errors } = useForm<FormSubmit>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    openModal === OpenModal.Edit ? refetchGroup() : null;
  }, [openModal]);

  const [editGroup] = useEditCurriculumGroupMutation({
    onCompleted() {
      refetch({
        ActualOn: addGMTOffsetToDate(currentDate),
      });
      closeModal();
      notifySuccess(t("common.сhange_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });

  const [createGroup] = useCreateCurriculumGroupMutation({
    onCompleted() {
      refetch({
        ActualOn: addGMTOffsetToDate(currentDate),
      });
      closeModal();
      notifySuccess(t("common.сhange_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });

  const submit: SubmitHandler<FormSubmit> = (data) => {
    const { name, status, sequenceNumber } = data;

    if (openModal === OpenModal.Edit) {
      editGroup({
        variables: {
          id: currentId,
          name,
          status: FormStatus[status.value],
          sequenceNumber: Number(sequenceNumber),
        },
      });
    } else if (openModal === OpenModal.Add) {
      createGroup({
        variables: {
          name,
          status: FormStatus[status.value],
          sequenceNumber: Number(sequenceNumber),
        },
      });
    }
  };

  useEffect(() => {
    if (currentGroup && openModal === OpenModal.Edit) {
      setValue("name", currentGroup.name);
      setValue("status", {
        label: transformStatus(currentGroup.status),
        value: currentGroup.status,
      });
      setValue("sequenceNumber", currentGroup.sequenceNumber);
    }
  }, [currentGroup, openModal]);

  useEffect(() => {
    group && setCurrentGroup(group as IGroup);
  }, [group]);

  return (
    <form id={formId} className={s.form} onSubmit={handleSubmit(submit)}>
      <div className={s.items}>
        <Field
          type="input"
          name="name"
          control={control}
          label={t("cep.name")}
          error={errors["name"]?.message}
        />
        <Field
          type="select"
          name="status"
          control={control}
          label={t("cep.status")}
          options={optionsStatus}
          labelInValue
          error={errors["status"]?.value?.message}
        />
        <Field
          type="input"
          name="sequenceNumber"
          control={control}
          label={t("cep.sequenceNumber")}
          error={errors["sequenceNumber"]?.message}
        />
      </div>
    </form>
  );
};
